'use client';

import { useEffect, useMemo, useRef, type CSSProperties } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { cn } from '@/lib/utils';
import { LocalDateTime } from '@/components/ui/LocalDateTime';
import { useHydratedTimeZone } from '@/hooks/useHydratedTimeZone';
import { GridMatchCard } from './GridMatchCard';
import { formatScheduledTime, type PublicMatch } from '../../libs/scheduleStatus';
import type { GridCourt } from '../../libs/slotGrid';
import {
  buildScheduleTimeline,
  normalizeTimelineZone,
  timelineNow,
} from '../../libs/scheduleTimeline';
import { VENUE_DOT_CLASSES, venueBands } from '../../libs/venueColumns';
import { dayKeyInZone } from '@/lib/dayKey';

interface SlotGridProps {
  matches: PublicMatch[];
  /** Full selected day, before venue filtering: owns the stable axis and scale. */
  axisMatches?: PublicMatch[];
  courts: GridCourt[];
  venues?: { id: string; name: string }[];
  onTap: (matchId: string) => void;
  day: string;
  /** Event day-bucketing zone; the axis labels use the hydrated display zone. */
  zone: string;
  nowMs: number | null;
}

/** Continuous elapsed-time calendar: cards, guides and NOW share one rem/minute scale. */
export function SlotGrid({
  matches,
  axisMatches = matches,
  courts,
  venues,
  onTap,
  day,
  zone,
  nowMs,
}: SlotGridProps) {
  const t = useTranslations('PublicSchedule');
  const locale = useLocale();
  const hydratedZone = useHydratedTimeZone();
  const displayZone = normalizeTimelineZone(
    hydratedZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
  );
  const timeline = useMemo(
    () =>
      buildScheduleTimeline(
        matches,
        courts.map((c) => c.id),
        displayZone,
        axisMatches,
      ),
    [matches, courts, displayZone, axisMatches],
  );
  const bands = useMemo(() => (venues ? venueBands(courts, venues) : null), [courts, venues]);
  const now = timeline && nowMs !== null ? timelineNow(timeline, nowMs, { day, zone }) : null;
  const nowRef = useRef<HTMLDivElement>(null);
  const didScrollRef = useRef(false);
  useEffect(() => {
    if (didScrollRef.current || !nowRef.current) return;
    nowRef.current.scrollIntoView({ block: 'center' });
    didScrollRef.current = true;
  });
  if (!timeline || !courts.length)
    return <p className="text-muted py-8 text-center text-sm">{t('noMatches')}</p>;
  const columns: CSSProperties = {
    gridTemplateColumns: `5rem ${courts.map(() => 'var(--court-col)').join(' ')}`,
  };
  const nowIso = nowMs === null ? '' : new Date(nowMs).toISOString();
  const firstDay = dayKeyInZone(new Date(timeline.startMs).toISOString(), displayZone);
  const tickTimes = timeline.ticks
    .filter((tick) => tick.major)
    .map((tick) => formatScheduledTime(new Date(tick.atMs).toISOString(), locale, displayZone));
  return (
    <div className="min-w-0">
      {/* On paper there is no ⓘ to open, so the note prints as a line. */}
      <p className="text-muted mb-2 hidden text-xs print:block">{t('timelineNote')}</p>
      <div className="bg-card border-border relative max-h-[85dvh] w-fit max-w-full overflow-auto rounded-xl border [--court-col:18rem] max-md:max-h-[70dvh] max-md:[--court-col:12rem] print:max-h-none print:overflow-visible">
        <div role="table" className="w-max min-w-full">
          <div className="bg-card border-border sticky top-0 z-10 border-b">
            {bands && (
              <div role="row" className="grid" style={columns}>
                <div role="columnheader" className="bg-card sticky left-0 z-20 px-2 py-1.5">
                  <span className="sr-only">{t('venueFilter')}</span>
                </div>
                {bands.map((b, i) => (
                  <div
                    role="columnheader"
                    data-testid="venue-band"
                    key={`${b.venueId ?? 'none'}-${i}`}
                    aria-colspan={b.span}
                    style={{ gridColumn: `span ${b.span}` }}
                    className="border-border flex items-center gap-1.5 border-b border-l px-2 py-1.5 text-xs font-bold break-words"
                  >
                    {b.colorIndex !== null && (
                      <span
                        aria-hidden="true"
                        className={cn(
                          'size-2.5 shrink-0 rounded-full',
                          VENUE_DOT_CLASSES[b.colorIndex],
                        )}
                      />
                    )}
                    {b.name}
                  </div>
                ))}
              </div>
            )}
            <div role="row" className="grid" style={columns}>
              <div
                role="columnheader"
                className="bg-card text-muted-foreground sticky left-0 z-20 flex items-center gap-1 px-2 py-2 font-mono text-[0.6875rem] font-semibold tracking-[0.06em] uppercase"
              >
                {t('timeHeader')}
                {/* The "scheduled times · estimated durations" note lives here, not as a line above the grid. The before: band makes the 22px icon a 44px tap target, reaching further down than up because the grid box clips above the header row. */}
                <PopoverPrimitive.Root>
                  <PopoverPrimitive.Trigger
                    aria-label={t('timelineNote')}
                    className="hover:text-foreground focus-visible:ring-ring relative -m-1 rounded-full p-1 before:absolute before:-inset-x-[11px] before:-top-[6px] before:-bottom-[18px] before:content-[''] focus-visible:ring-2 focus-visible:outline-none print:hidden"
                  >
                    <InformationCircleIcon aria-hidden="true" className="size-3.5" />
                  </PopoverPrimitive.Trigger>
                  <PopoverPrimitive.Portal>
                    <PopoverPrimitive.Content
                      align="start"
                      sideOffset={6}
                      collisionPadding={16}
                      hideWhenDetached
                      className="border-border bg-card text-muted z-50 max-w-[calc(100vw-2rem)] rounded-lg border px-3 py-2 text-xs shadow-lg"
                    >
                      {t('timelineNote')}
                    </PopoverPrimitive.Content>
                  </PopoverPrimitive.Portal>
                </PopoverPrimitive.Root>
              </div>
              {courts.map((c) => (
                <div
                  role="columnheader"
                  key={c.id}
                  className="border-border flex items-start justify-between gap-1 border-l px-2 py-2 text-xs font-bold"
                >
                  <span className="min-w-0 break-words">{c.name}</span>
                  {c.delayMin > 0 && (
                    <span className="text-warning-emphasis shrink-0 font-mono text-[0.6875rem]">
                      <span aria-hidden="true">{t('delayShort', { minutes: c.delayMin })}</span>
                      <span className="sr-only">{t('delayedBy', { minutes: c.delayMin })}</span>
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="py-8">
            <div
              role="row"
              data-testid="schedule-timeline"
              data-start-ms={timeline.startMs}
              data-end-ms={timeline.endMs}
              data-rem-per-minute={timeline.remPerMinute}
              className="relative grid"
              style={{ ...columns, height: `${timeline.heightRem}rem` }}
            >
              <div role="rowheader" className="bg-card sticky left-0 z-[8]">
                {timeline.ticks.map((tick) => {
                  const iso = new Date(tick.atMs).toISOString();
                  const collision = now && Math.abs(tick.topRem - now.topRem) < 2;
                  const date = dayKeyInZone(iso, displayZone);
                  const timeText = formatScheduledTime(iso, locale, displayZone);
                  const repeated =
                    tick.major && tickTimes.filter((time) => time === timeText).length > 1;
                  return (
                    <div
                      key={tick.atMs}
                      data-testid="schedule-time-tick"
                      data-at-ms={tick.atMs}
                      data-major={tick.major}
                      className="absolute inset-x-0 -translate-y-1/2 px-2 text-right"
                      style={{ top: `${tick.topRem}rem` }}
                    >
                      {tick.major && !collision && (
                        <span className="block font-mono text-xs font-semibold tabular-nums">
                          {date !== firstDay && (
                            <span className="text-muted-foreground block text-[0.625rem]">
                              {new Intl.DateTimeFormat(locale, {
                                timeZone: displayZone,
                                month: 'short',
                                day: 'numeric',
                              }).format(new Date(iso))}
                            </span>
                          )}
                          <LocalDateTime
                            iso={iso}
                            locale={locale}
                            format={(iso, locale) => formatScheduledTime(iso, locale, displayZone)}
                          />
                          {repeated && (
                            <span className="text-muted-foreground block text-[0.625rem]">
                              {
                                new Intl.DateTimeFormat(locale, {
                                  timeZone: displayZone,
                                  timeZoneName: 'shortOffset',
                                })
                                  .formatToParts(new Date(iso))
                                  .find((p) => p.type === 'timeZoneName')?.value
                              }
                            </span>
                          )}
                        </span>
                      )}
                    </div>
                  );
                })}
                {now && (
                  <span
                    className="absolute left-1 flex -translate-y-1/2"
                    style={{ top: `${now.topRem}rem` }}
                  >
                    <NowPill
                      label={t('now')}
                      iso={nowIso}
                      locale={locale}
                      displayZone={displayZone}
                    />
                  </span>
                )}
              </div>
              {courts.map((court, index) => (
                <div
                  key={court.id}
                  role="cell"
                  data-court-id={court.id}
                  data-lane-count={timeline.laneCounts[index]}
                  className="border-border relative border-l"
                >
                  {timeline.placements
                    .filter((p) => p.courtIndex === index)
                    .map((p) => (
                      <div
                        key={p.match.id}
                        data-testid="schedule-timeline-match"
                        data-match-id={p.match.id}
                        data-court-id={court.id}
                        data-lane={p.lane}
                        data-overlap-count={p.laneCount}
                        className="@container/tile absolute z-[6] px-1 py-px"
                        style={{
                          top: `${p.topRem}rem`,
                          height: `${p.heightRem}rem`,
                          left: `${(p.lane / p.laneCount) * 100}%`,
                          width: `${100 / p.laneCount}%`,
                        }}
                      >
                        <GridMatchCard
                          match={p.match}
                          onTap={onTap}
                          timeline={{
                            endIso: new Date(p.endMs).toISOString(),
                            compact: p.heightRem < 7,
                            roomy: p.heightRem >= 10,
                          }}
                        />
                      </div>
                    ))}
                </div>
              ))}
              {timeline.ticks.map((tick) => (
                <div
                  key={tick.atMs}
                  aria-hidden="true"
                  className={cn(
                    'border-border pointer-events-none absolute right-0 left-[5rem] border-t',
                    !tick.major && 'border-dashed opacity-50',
                  )}
                  style={{ top: `${tick.topRem}rem` }}
                />
              ))}
              {now && (
                <div
                  ref={nowRef}
                  aria-hidden="true"
                  data-testid="schedule-now-line"
                  data-edge={now.edge ?? undefined}
                  className="border-danger pointer-events-none absolute inset-x-0 z-[7] h-0 scroll-mt-24 border-t-2"
                  style={{ top: `${now.topRem}rem` }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function NowPill({
  label,
  iso,
  locale,
  displayZone,
}: {
  label: string;
  iso: string;
  locale: string;
  displayZone: string;
}) {
  return (
    <span
      data-testid="schedule-now"
      className="bg-danger text-danger-foreground inline-block rounded px-1.5 py-px font-mono text-[0.6875rem] leading-snug font-bold whitespace-nowrap tabular-nums"
    >
      <span className="sr-only">{label} · </span>
      <LocalDateTime
        iso={iso}
        locale={locale}
        format={(iso, locale) => formatScheduledTime(iso, locale, displayZone)}
      />
    </span>
  );
}
